import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { fmtMT } from "@/lib/mountainTime";
import { emailSettings, saveEmailSettings, emailDeliveries, emailTest, errText } from "@/lib/bidCenterApi";

const ESTATUS = { preview_not_sent: "Preview — logged, not sent", auction_email_off: "Not sent — automatic auction email off", skipped_other_environment: "Not sent — saved in another environment",
  sent: "Sent", failed: "Failed", sending: "Sending…", blocked_preview_allowlist: "Not sent — Preview only emails allow-listed addresses" };
const tone = (s) => (s === "sent" ? "bg-emerald-100 text-emerald-900" : s === "failed" ? "bg-red-100 text-red-900" : "bg-stone-100 text-stone-700");

function EmailRecipient({ r, i, labels, onChange, onRemove }) {
  const set = (k, v) => onChange({ ...r, [k]: v });
  return (
    <div data-testid={`email-recipient-${i}`} className="rounded-lg border p-3 space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
        <Input data-testid={`email-recipient-name-${i}`} value={r.name} onChange={(e) => set("name", e.target.value)} placeholder="Name" className="h-11 sm:h-9" />
        <Input data-testid={`email-recipient-email-${i}`} type="email" value={r.email} onChange={(e) => set("email", e.target.value)} placeholder="Email address" className="h-11 sm:h-9" />
        <Button variant="ghost" data-testid={`email-recipient-remove-${i}`} onClick={onRemove} className="h-11 sm:h-9"><Trash2 className="h-4 w-4" /></Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {Object.entries(labels).map(([k, l]) => (
          <label key={k} className="text-xs flex items-center gap-1 rounded-full border px-2 py-1">
            <input type="checkbox" data-testid={`email-recipient-${i}-event-${k}`} checked={r.events.includes(k)} onChange={(e) => set("events", e.target.checked ? [...r.events, k] : r.events.filter((x) => x !== k))} />{l}
          </label>
        ))}
      </div>
      <label className="text-xs flex items-center gap-2"><Switch checked={r.active} onCheckedChange={(v) => set("active", v)} data-testid={`email-recipient-active-${i}`} />Active</label>
    </div>
  );
}

function EmailLogItem({ d }) {
  return (
    <details data-testid={`email-log-${d.id}`} className="rounded-lg border bg-white p-3 text-sm">
      <summary className="cursor-pointer flex flex-wrap items-center gap-2">
        <span className="font-medium">{d.subject}</span>
        <span data-testid={`email-log-status-${d.id}`} className={`rounded-full px-2 py-0.5 text-xs ${tone(d.status)}`}>{ESTATUS[d.status] || d.status}</span>
        <span className="text-xs text-stone-500 w-full sm:w-auto">To {d.recipient_name} &lt;{d.to}&gt; · {fmtMT(d.created_at)}</span>
      </summary>
      {d.error && <p data-testid={`email-log-error-${d.id}`} className="mt-2 text-xs text-red-800">{d.error}</p>}
      <iframe title={`email-${d.id}`} data-testid={`email-log-html-${d.id}`} sandbox="" srcDoc={d.html} className="mt-2 w-full h-80 rounded border bg-white" />
    </details>
  );
}

export function EmailNotifications() {
  const [s, setS] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => { emailSettings().then(setS).catch((e) => toast.error(errText(e))); emailDeliveries().then(setLog).catch(() => {}); };
  useEffect(() => { load(); }, []);
  if (!s) return null;
  const save = async () => { try { setS(await saveEmailSettings({ recipients: s.recipients })); toast.success("Email settings saved."); } catch (e) { toast.error(errText(e)); } };
  const test = async (real) => {
    if (real && !window.confirm(`Send ONE real test email to ${s.my_email} now?`)) return;
    setBusy(true);
    try { const r = await emailTest({ send_real: real, confirm: real }); toast.success(ESTATUS[r.status] || r.status); load(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <section data-testid="email-notifications" className="rounded-xl border bg-white p-4 space-y-3">
      <p className="font-semibold flex items-center gap-2"><Mail className="h-4 w-4" />Email notifications</p>
      <p data-testid="email-state" className="text-xs text-stone-600">
        Sender: {s.sender || "not configured"}. {s.preview ? "Preview: auction emails are logged below with their exact content and never sent." : s.auction_email_enabled ? "Automatic auction email is ON." : "Automatic auction email is OFF: emails are logged, not sent."}
      </p>
      {s.recipients.map((r, i) => <EmailRecipient key={r.id || i} i={i} r={r} labels={s.event_labels} onChange={(nr) => setS({ ...s, recipients: s.recipients.map((x, j) => (j === i ? nr : x)) })} onRemove={() => setS({ ...s, recipients: s.recipients.filter((_, j) => j !== i) })} />)}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" data-testid="email-add-recipient" onClick={() => setS({ ...s, recipients: [...s.recipients, { name: "", email: "", events: Object.keys(s.event_labels), active: true }] })} className="h-11 sm:h-9"><Plus className="h-4 w-4 mr-1" />Add email recipient</Button>
        <Button variant="outline" data-testid="email-test-preview" disabled={busy} onClick={() => test(false)} className="h-11 sm:h-9"><Mail className="h-4 w-4 mr-1" />Preview test email</Button>
        <Button variant="outline" data-testid="email-test-real" disabled={busy || !s.provider_configured} onClick={() => test(true)} className="h-11 sm:h-9"><Send className="h-4 w-4 mr-1" />Send real test to me</Button>
      </div>
      <Button data-testid="email-save" onClick={save} className="w-full h-11 bg-amber-800 hover:bg-amber-900 text-white">Save email settings</Button>
      <div className="space-y-2" data-testid="email-log">
        <p className="font-semibold text-sm">Email delivery log</p>
        {!log.length && <p className="text-sm text-stone-500" data-testid="email-log-empty">No emails yet.</p>}
        {log.slice(0, 50).map((d) => <EmailLogItem key={d.id} d={d} />)}
      </div>
    </section>
  );
}
